<template>
  <div class="relative">
    <Input
      v-model="searchInput"
      :class="{ 'border-red-400': !isInputValid && searchInput }"
      placeholder="Enter city"
      :disabled="disabled"
      icon="i-heroicons-magnifying-glass"
      @click="onCityClick"
      @update:model-value="onSearch"
      @blur="handleInputBlur"
    />
    <div
      v-if="isClient && showSuggestions && filteredCities.length > 0"
      class="suggestions-box"
    >
      <button
        v-for="city in filteredCities"
        :key="city.name"
        class="suggestion-item"
        @click.stop="selectCity(city)"
        @mousedown.prevent
      >
        {{ city.name }}
        <span v-if="city.jobCount" class="text-gray-400 text-sm ml-1">
          ({{ city.jobCount }} {{ city.jobCount === 1 ? 'job' : 'jobs' }})
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onUnmounted,
  onMounted
} from 'vue'

export interface City {
  name: string
  county?: string
  population?: number
  jobCount?: number
}
const props = defineProps<{
  stateCode?: string
  stateName?: string
  initialCity?: string
  disabled?: boolean
  preFetchedCities?: City[]
}>()
const emit = defineEmits<{
  'update:selected': [City | null]
  'update:input': [string]
  'update:isValid': [boolean]
  'update:exactMatch': [boolean]
}>()
const cities = ref<City[]>([])
const selectedCity = ref<City | null>(null)
const searchInput = ref(props.initialCity || '')
const showSuggestions = ref(false)
const isLoading = ref(false)
const blurTimeout = ref<number | null>(null)
const isMounted = ref(false)
const isClient = ref(false)
const isInputValid = computed(() => {
  if (!searchInput.value.trim()) return true
  if (props.disabled || cities.value.length === 0) return true
  const normalizedInput = searchInput.value.toLowerCase().trim()
  return !!cities.value.find(
    city => city.name.toLowerCase().trim() === normalizedInput
  )
})
watch(isInputValid, (valid) => {
  emit('update:isValid', valid)
})
watch(
  () => props.preFetchedCities,
  (newCities, oldCities) => {
    if (newCities && Array.isArray(newCities) && newCities.length > 0) {
      const formattedCities = newCities.map((city) => {
        if (typeof city === 'string') {
          return { name: city }
        }
        return city
      })
      cities.value = formattedCities
      if (props.initialCity && isMounted.value) {
        const cityOption = findCityByName(props.initialCity)
        if (cityOption) {
          selectCity(cityOption)
        }
      }
    } else {
      if (newCities !== undefined && newCities !== null && oldCities !== undefined) {
        cities.value = []
        if (searchInput.value && !props.initialCity) {
          searchInput.value = ''
          emit('update:input', '')
          emit('update:selected', null)
        }
      }
    }
  },
  { immediate: true }
)
watch(
  () => props.stateCode,
  async (newStateCode, oldStateCode) => {
    if (newStateCode !== oldStateCode) {
      clearSelection()
      if (newStateCode && !props.preFetchedCities) {
        await fetchCitiesForState(newStateCode)
      }
    }
  }
)
const filteredCities = computed(() => {
  const query = searchInput.value?.toLowerCase() || ''
  if (!query) return cities.value.slice(0, 10)
  return cities.value
    .filter(city => city.name?.toLowerCase().includes(query))
    .slice(0, 10)
})
function selectCity(city: City) {
  selectedCity.value = city
  searchInput.value = city.name
  showSuggestions.value = false
  emit('update:selected', city)
  emit('update:input', city.name)
  emit('update:isValid', true)
  emit('update:exactMatch', true)
}
function clearSelection() {
  selectedCity.value = null
  searchInput.value = ''
  emit('update:selected', null)
  emit('update:input', '')
  emit('update:exactMatch', false)
}
function onCityClick() {
  if (!props.disabled && cities.value.length > 0) {
    showSuggestions.value = true
  }
}
function handleInputBlur() {
  blurTimeout.value = window.setTimeout(() => {
    showSuggestions.value = false
    if (searchInput.value && !isInputValid.value) {
      clearSelection()
    }
  }, 150)
}
function onSearch(value: string) {
  searchInput.value = value
  if (
    !props.disabled
    && cities.value.length > 0
    && document.activeElement === document.querySelector('input')
  ) {
    showSuggestions.value = true
  }
  emit('update:input', value)
  const normalizedInput = value.toLowerCase().trim()
  const exactMatch = cities.value.find(
    city => city.name.toLowerCase().trim() === normalizedInput
  )
  emit('update:exactMatch', !!exactMatch)
  emit('update:isValid', !value.trim() || !!exactMatch || props.disabled)
  if (!value) {
    selectedCity.value = null
  } else if (exactMatch) {
    selectedCity.value = exactMatch
  }
}
async function fetchCitiesForState(stateCode: string) {
  if (!stateCode) return
  isLoading.value = true
  try {
    const stateParam = props.stateName?.toLowerCase().replace(/\s+/g, '-') || stateCode.toLowerCase()
    const response = await $fetch<{ success: boolean, cities: City[] }>(`/api/locations/cities/${stateParam}`)
    if (response.success && response.cities) {
      cities.value = response.cities
      if (props.initialCity && isMounted.value) {
        const cityOption = findCityByName(props.initialCity)
        if (cityOption) {
          selectCity(cityOption)
        }
      }
    }
  } catch (error) {
    console.error('Error fetching cities:', error)
    cities.value = []
  } finally {
    isLoading.value = false
  }
}
function findCityByName(cityName: string): City | null {
  if (!cityName || cities.value.length === 0) return null
  const normalizedName = cityName.toLowerCase().trim()
  const exactMatch = cities.value.find(
    city => city.name.toLowerCase().trim() === normalizedName
  )
  if (exactMatch) return exactMatch
  const partialMatch = cities.value.find(city =>
    city.name.toLowerCase().trim().includes(normalizedName)
  )
  return partialMatch || null
}
function triggerLoadCities() {
  if (props.stateCode && !props.preFetchedCities) {
    fetchCitiesForState(props.stateCode)
  }
}
onMounted(async () => {
  isClient.value = true
  if (import.meta.client) {
    document.addEventListener('click', handleOutsideClick)
  }
  emit('update:isValid', isInputValid.value)
  emit('update:exactMatch', !!selectedCity.value)
  if (props.stateCode && !props.preFetchedCities && cities.value.length === 0) {
    await fetchCitiesForState(props.stateCode)
  }
  if (props.initialCity && cities.value.length > 0) {
    const cityOption = findCityByName(props.initialCity)
    if (cityOption) {
      selectCity(cityOption)
    }
  }
  isMounted.value = true
})
function handleOutsideClick(event: Event) {
  const target = event.target as HTMLElement
  if (!target.closest('.suggestions-box') && !target.closest('input')) {
    showSuggestions.value = false
  }
}
onUnmounted(() => {
  if (import.meta.client) {
    document.removeEventListener('click', handleOutsideClick)
  }
  if (blurTimeout.value !== null) {
    clearTimeout(blurTimeout.value)
  }
})
defineExpose({
  searchInput,
  cities,
  findCityByName,
  selectedCity,
  isInputValid,
  clearSelection,
  triggerLoadCities
})
</script>

<style scoped>
.suggestions-box {
  @apply absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto;
}

.suggestion-item {
  @apply w-full px-4 py-2 text-left hover:bg-gray-100 flex justify-between items-center;
}
</style>
